// ChartGeneric.jsx
import React from 'react'
import PropTypes from 'prop-types'
import { Chart } from 'react-google-charts'
import { injectIntl } from 'react-intl'
import Spinner from '../../../util/spinner-shim'

import {
  chartDimensions,
  displayFormula,
} from 'altrac-conversions'

// HOC
import withAppStore from '../../../util/withAppStore'

import NoChartData from './NoChartData'
import styles from '../../../../css/charts.module.css'

const formatTooltipValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return 'N/A'
  }
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return 'N/A'
    try {
      return value.toLocaleString()
    } catch {
      // Some devices/browsers ship with a malformed default Intl locale,
      // which throws "failed to initialize DateTimeFormat" on toLocaleString().
      return String(value)
    }
  }
  return value
}

const normalizeChartValue = (value) => {
  // Preserve missing values as gaps in the chart.
  // JS numeric coercion turns null/'' into 0, which is misleading for telemetry.
  if (value === null || value === undefined || value === '') {
    return null
  }
  if (typeof value === 'string' && value.trim() === '') {
    return null
  }
  if (typeof value === 'string' && (value.toLowerCase() === 'null' || value === 'NaN')) {
    return null
  }
  if (value === 'OFF' || value === 'Off' || value === 'STP' || value === 'ERL' || value === 'ERH' || value === 'EMP') {
    return 0
  }
  if (value === 'ON' || value === 'RUN' || value === 'Low' || value === 'LOW' || value === 'FULL') {
    return 1
  }
  if (value === 'High' || value === 'HIGH') {
    return 2
  }
  const numericValue = Number(value)
  if (Number.isNaN(numericValue)) {
    return null
  }
  return numericValue
}

const formatTooltipDate = (timestamp) => {
  const date = new Date(timestamp)
  try {
    return date.toLocaleString()
  } catch {
    // Some devices/browsers ship with a malformed default Intl locale,
    // which throws "failed to initialize DateTimeFormat" on toLocaleString().
    return date.toISOString()
  }
}

const buildTooltip = (timestamp, entries = []) => {
  const dateString = formatTooltipDate(timestamp)
  const rowsMarkup = entries.length
    ? entries.map(({ label, value }) => `
        <div class="${styles.chartTooltipRow}">
          <span class="${styles.chartTooltipLabel}">${label}</span>
          <span class="${styles.chartTooltipValue}">${value}</span>
        </div>
      `).join('')
    : `<div class="${styles.chartTooltipEmpty}">No data</div>`

  return `
    <div class="${styles.chartTooltip}">
      <div class="${styles.chartTooltipHeader}">${dateString}</div>
      <div class="${styles.chartTooltipBody}">
        ${rowsMarkup}
      </div>
    </div>
  `
}

export const makeRows = function makeRows (
  sensorData,
  dataName,
  device,
  dataType,
  conversion,
  tempConv,
  multiplier,
  precision,
  text = false
) {
  const rows = []
  if (
    sensorData.length > 0
  ) {
    for (let i = 0; i < sensorData.length; i += 1) {
      const row = []
      let lastSensorData = sensorData[i]
      if (i < sensorData.length - 1) {
        lastSensorData = sensorData[i + 1]
      }
      if (Array.isArray(dataType)) {
        row[0] = new Date(sensorData[i].date)
        const tooltipEntries = []
        let hasValue = false
        for (let j = 0; j < dataType.length; j += 1) {
          if (sensorData[i][dataType[j]] || sensorData[i][dataType[j]] === 0) {
            let value
            if (sensorData[i] && sensorData[i].hasOwnProperty(dataType[j])) {
              value = sensorData[i][dataType[j]]
            } else {
              value = 0
            }
            if (conversion) {
              let formula = conversion
              if (Array.isArray(conversion)) {
                formula = conversion[j]
              }
              let multiplierValue = 1
              if (multiplier) {
                multiplierValue = multiplier
              }
              if (Array.isArray(multiplier)) {
                multiplierValue = multiplier[j]
              }
              let precisionValue = 1
              if (precision) {
                precisionValue = precision
              }
              if (Array.isArray(precision)) {
                precisionValue = precision[j]
              }
              value = displayFormula(
                formula,
                multiplierValue,
                precisionValue,
                { tempConv },
                dataType[j],
                sensorData[i] || {},
                lastSensorData || {},
                device.physical || {}
              )
            } else {
              value = sensorData[i][dataType[j]]
            }
            const labelText = Array.isArray(dataName) ? dataName[j] : dataName
            const tooltipValue = formatTooltipValue(value)
            const chartValue = text ? value : normalizeChartValue(value)
            tooltipEntries.push({
              label: labelText,
              value: tooltipValue,
            })
            row[j + 2] = text ? value : chartValue
            // Keep the row even when some series are missing (null) so the chart shows gaps.
            // Only count it as "having values" when at least one series is present.
            if (text || chartValue !== null) {
              hasValue = true
            }
          } else {
            row[j + 2] = null
          }
        }
        if (hasValue === true && (row.length) === (dataType.length + 2)) {
          row[1] = buildTooltip(sensorData[i].date, tooltipEntries)
          rows.push(row)
        }
      } else if (sensorData[i][dataType] || sensorData[i][dataType] === 0) {
        let value = sensorData[i][dataType]
        lastSensorData = sensorData[i]
        if (i < sensorData.length - 1) {
          lastSensorData = sensorData[i + 1]
        }
        if (conversion) {
          value = displayFormula(
            conversion,
            multiplier,
            precision,
            { tempConv },
            dataType,
            sensorData[i] || {},
            lastSensorData || {},
            device.physical || {}
          )
        } else {
          value = sensorData[i][dataType]
        }
        const tooltipValue = formatTooltipValue(value)
        const chartValue = text ? value : normalizeChartValue(value)
        row[0] = new Date(sensorData[i].date)
        row[1] = buildTooltip(sensorData[i].date, [{
          label: Array.isArray(dataName) ? dataName[0] : dataName,
          value: tooltipValue,
        }])
        row[2] = text ? value : chartValue
        rows.push(row)
      }
    }
  }
  return rows
}

export const makeColumns = function makeColumns (dataName, dataType, formatMessage) {
  let time = 'Time'
  if (typeof formatMessage === 'function') {
    time = formatMessage({
      id: 'sensor.time.short',
      defaultMessage: 'Time',
      description: 'Time',
    })
  }
  if (!Array.isArray(dataType)) {
    return ([
      {
        type: 'date',
        label: time,
        role: 'domain',
      },
      {
        type: 'string',
        role: 'tooltip',
        p: { html: true },
      },
      {
        type: 'number',
        label: dataName,
      },
    ])
  }
  const columns = []
  columns[0] = {
    type: 'date',
    label: time,
  }
  columns[1] = {
    type: 'string',
    role: 'tooltip',
    p: { html: true },
  }
  for (let i = 0; i < dataType.length; i += 1) {
    let dataNameIterator = dataName
    if (Array.isArray(dataName)) {
      dataNameIterator = dataName[i]
    }
    columns[i + 2] = {
      type: 'number',
      label: dataNameIterator,
    }
  }
  return columns
}

const ChartGeneric = function ChartGeneric ({
  sensorData,
  metricReady,
  device,
  dataType,
  dataName,
  conversion,
  multiplier,
  precision,
  vMax,
  vMin,
  stacked,
  chartType,
  intl: { formatMessage },
  tempConv,
}) {
  if (!metricReady) {
    return (
      <div className='loading'>
        <Spinner name='double-bounce' />
      </div>
    )
  }

  let stackedChart = true
  if (stacked || stacked === false) {
    stackedChart = stacked
  }
  const options = {
    hAxis: {
      slantedText: false,
      gridlines: {
        count: -1,
        units: {
          days: { format: ['M/d'] },
          hours: { format: ['M/d ha'] },
        },
      },
      minorGridlines: {
        units: {
          hours: { format: ['ha'] },
          minutes: { format: ['HH:mm a Z', ':mm'] },
        },
      },
    },
    vAxis: {
      textPosition: 'in',
    },
    chartArea: {
      left: 0,
      top: 0,
      width: '100%',
      height: '85%',
    },
    isStacked: stackedChart,
    legend: { position: 'bottom' },
    lineWidth: 4,
    // This line makes the entire category's tooltip active.
    focusTarget: 'category',
    // Use an HTML tooltip.
    tooltip: {
      isHtml: true,
      trigger: 'focus',
    },
  }

  if (vMax !== null) {
    options.vAxis.maxValue = vMax
  }

  if (vMin !== null) {
    options.vAxis.minValue = vMin
  }

  const rows = makeRows(sensorData, dataName, device, dataType, conversion, tempConv, multiplier, precision)
  if (rows.length < 1) {
    return (
      <NoChartData />
    )
  }

  const columns = makeColumns(dataName, dataType, formatMessage)

  const dimensions = chartDimensions(window.innerWidth)

  let chartTypeSet = 'AreaChart'
  if (chartType) {
    chartTypeSet = chartType
  }

  return (
    <div className={styles.chart}>
      <Chart
        chartType={chartTypeSet}
        rows={rows}
        width={dimensions.width}
        height={dimensions.height}
        columns={columns}
        options={options}
        style={{
          margin: '0 auto',
          position: 'relative',
          top: '5px',
        }}
      />
    </div>
  )
}

ChartGeneric.propTypes = {
  sensorData: PropTypes.arrayOf(PropTypes.object),
  metricReady: PropTypes.bool,
  dataType: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.array,
  ]),
  dataName: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.array,
  ]),
  multiplier: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.array,
  ]),
  precision: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.array,
  ]),
  conversion: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.array,
  ]),
  vMax: PropTypes.number,
  vMin: PropTypes.number,
  stacked: PropTypes.bool,
  chartType: PropTypes.string,
  tempConv: PropTypes.string.isRequired,
}

export default withAppStore(injectIntl(ChartGeneric))
